import { Tooltip } from '@base-ui/react/tooltip';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import type { ReactNode } from 'react';
import faviconUrl from '@/assets/images/job-goblin.ico?url';
import { DefaultCatchBoundary } from '@/components/DefaultCatchBoundary';
import { NotFound } from '@/components/NotFound';
import { ToastProvider } from '@/providers/toast-provider';
import appCss from '@/styles.css?url';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    component: RootComponent,
    errorComponent: (props) => (
      <RootDocument>
        <DefaultCatchBoundary {...props} />
      </RootDocument>
    ),
    head: () => ({
      links: [
        {
          href: appCss,
          rel: 'stylesheet',
        },
        {
          href: faviconUrl,
          rel: 'icon',
        },
      ],
      meta: [
        { charSet: 'utf-8' },
        {
          content: 'width=device-width, initial-scale=1',
          name: 'viewport',
        },
        { title: 'Search Dashboard' },
      ],
    }),
    notFoundComponent: () => <NotFound />,
  },
);

function RootComponent() {
  return (
    <RootDocument>
      <Tooltip.Provider closeDelay={60} delay={250}>
        <ToastProvider>
          <Outlet />
        </ToastProvider>
      </Tooltip.Provider>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body className="bg-background font-mono text-foreground antialiased">
        {children}
        <TanStackRouterDevtools position="bottom-right" />
        <ReactQueryDevtools buttonPosition="bottom-left" />
        <Scripts />
      </body>
    </html>
  );
}
